/**
 * ResearchStrategies.jsx — one table, one detail panel (HANDOFF §5.3).
 *
 * Replaces the Pipeline kanban, the Discovery leaderboard, the tournament
 * board, governance and the deployment board. All five were views of the same
 * strategies through different queries, which is why they disagreed.
 *
 * Missing values render as an em dash. Never 0 for unknown.
 */

import React, { useMemo, useState } from "react";

import { fmtInt, fmtNum, fmtPct, fmtRate } from "../../lib/format";
import { useStrategies } from "../../lib/researchApi";

const DASH = "—";

const STAGE_CHIPS = [
  ["", "All"],
  ["promoted", "Promoted"],
  ["testing", "In testing"],
  ["forward", "Forward test"],
  ["approved", "Approved"],
  ["retired", "Retired"],
];

const GATE_ORDER = ["sample", "edge", "breadth", "fwd_sample", "holds_up", "unique"];
const GATE_LABEL = {
  sample: "Sample", edge: "Edge", breadth: "Breadth",
  fwd_sample: "Forward sample", holds_up: "Holds up", unique: "Unique",
};

const STAGE_TONE = {
  approved: "var(--accent)", forward: "var(--info)", promoted: "var(--pos)",
  testing: "var(--muted)", retired: "var(--neg)",
};

function StageChip({ stage }) {
  return (
    <span className="rx-chip rx-chip-static"
          style={{ color: STAGE_TONE[stage] || "var(--muted)", padding: "3px 9px",
                   minHeight: 0, fontSize: 10.5 }}>
      {stage || DASH}
    </span>
  );
}

function Gates({ gates }) {
  const passed = GATE_ORDER.filter((g) => gates?.[g] === "pass").length;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
      <span className="rx-gates">
        {GATE_ORDER.map((g) => (
          <span key={g}
                className={`rx-gate rx-gate-${gates?.[g] || "pending"}`}
                title={`${GATE_LABEL[g]}: ${gates?.[g] || "pending"}`} />
        ))}
      </span>
      <span className="rx-num rx-faint" style={{ fontSize: 11 }}>{passed}/6</span>
    </span>
  );
}

function NetCell({ v }) {
  if (v == null) return <span className="rx-faint">{DASH}</span>;
  return <span style={{ color: v < 0 ? "var(--neg)" : "var(--pos)" }}>{fmtPct(v)}</span>;
}

/* ── Detail panel ─────────────────────────────────────────────────────────── */

function Kpi({ label, value }) {
  return (
    <div className="rx-cell" style={{ padding: "9px 11px" }}>
      <div className="rx-label" style={{ fontSize: 11 }}>{label}</div>
      <div className="rx-num" style={{ fontSize: 15, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function GateRow({ gate, status, rule, note }) {
  const tone = status === "pass" ? "var(--pos)" : status === "fail" ? "var(--neg)" : "var(--muted)";
  return (
    <div style={{ display: "flex", gap: 10, padding: "9px 0", borderBottom: "1px solid var(--row-line)" }}>
      <span className="rx-tag" style={{ background: "transparent", color: tone,
                                        border: `1px solid ${tone}`, flexShrink: 0, minWidth: 58,
                                        textAlign: "center" }}>
        {status || "pending"}
      </span>
      <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
        <strong>{gate}</strong>
        <div className="rx-muted" style={{ fontSize: 11.5 }}>{rule}</div>
        {note && <div className="rx-faint rx-num" style={{ fontSize: 11, marginTop: 2 }}>{note}</div>}
      </div>
    </div>
  );
}

function Detail({ s, bar, onClose }) {
  if (!s) return null;
  const bt = s.backtest || {}, fw = s.forward || {};
  const tpp = bt.trades && bt.profitable_pairs ? bt.trades / bt.profitable_pairs : null;

  const notes = {
    sample: `${fmtInt(bt.trades)} trades`,
    edge: bt.net_per_trade_pct == null ? "no net figure reported" : `${fmtPct(bt.net_per_trade_pct)} per trade`,
    breadth: tpp == null ? "no pair data" : `${fmtNum(tpp, 1)} trades per profitable pair`,
    fwd_sample: `${fmtInt(fw.trades || 0)} forward trades`,
    holds_up: fw.net_per_trade_pct == null ? "no forward result yet" : `forward ${fmtPct(fw.net_per_trade_pct)}`,
    unique: s.identity === "behaviour" ? "identity confirmed by signal hash"
      : s.identity === "params" ? "identity from template and parameters"
        : "identity from name only",
  };
  const rules = {
    sample: `at least ${fmtInt(bar?.min_trades)} backtest trades`,
    edge: "net return per trade positive after fees",
    breadth: `at least ${bar?.min_trades_per_pair} trades per profitable pair`,
    fwd_sample: `at least ${bar?.min_forward_trades} forward-test trades`,
    holds_up: `forward edge at least ${Math.round((bar?.holdup_ratio || 0) * 100)}% of backtest`,
    unique: "no unresolved duplicate of this strategy",
  };

  return (
    <aside className="rx-detail">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
          <StageChip stage={s.stage} />
          <span className="rx-faint" style={{ fontSize: 11, overflow: "hidden",
                                              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {s.author || "author unknown"}
          </span>
        </div>
        <button type="button" className="rx-chip" onClick={onClose} aria-label="Close detail">✕</button>
      </div>

      <h2 className="rx-display" style={{ fontSize: 26, margin: "12px 0 4px" }}>
        {s.name || "Unnamed strategy"}
      </h2>
      {s.aliases?.length > 1 && (
        <div className="rx-faint" style={{ fontSize: 11, marginBottom: 8 }}>
          also promoted as {s.aliases.filter((a) => a !== s.name).join(", ")}
        </div>
      )}

      <div className="rx-cell" style={{ borderColor: "var(--accent)", margin: "12px 0" }}>
        <div className="rx-label" style={{ fontSize: 11 }}>Next step</div>
        <div style={{ fontSize: 13.5, marginTop: 5, lineHeight: 1.5 }}>{s.next_step}</div>
      </div>

      <div className="rx-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <Kpi label="Trades" value={bt.trades == null ? DASH : fmtInt(bt.trades)} />
        <Kpi label="Net/trade" value={bt.net_per_trade_pct == null ? DASH : fmtPct(bt.net_per_trade_pct)} />
        <Kpi label="Win rate" value={bt.win_rate == null ? DASH : fmtRate(bt.win_rate)} />
        <Kpi label="Sharpe" value={bt.sharpe == null ? DASH : fmtNum(bt.sharpe, 2)} />
        <Kpi label="Profitable pairs" value={bt.profitable_pairs == null ? DASH : fmtInt(bt.profitable_pairs)} />
        <Kpi label="Trades/pair" value={tpp == null ? DASH : fmtNum(tpp, 1)} />
      </div>

      <div className="rx-label" style={{ marginTop: 18, marginBottom: 4 }}>The deploy bar</div>
      {GATE_ORDER.map((g) => (
        <GateRow key={g} gate={GATE_LABEL[g]} status={s.gates?.[g]} rule={rules[g]} note={notes[g]} />
      ))}

      <div className="rx-label" style={{ marginTop: 18, marginBottom: 6 }}>Backtest vs forward</div>
      <p className="rx-muted" style={{ fontSize: 12.5, lineHeight: 1.6, margin: 0 }}>
        {fw.trades
          ? `Backtest ${fmtPct(bt.net_per_trade_pct)} per trade over ${fmtInt(bt.trades)} trades. Forward ${fmtPct(fw.net_per_trade_pct)} per trade over ${fmtInt(fw.trades)} live trades, ${fmtPct(fw.net_total_pct)} on the book.`
          : `Backtest only: ${fmtPct(bt.net_per_trade_pct)} per trade over ${fmtInt(bt.trades)} trades. No forward test has run, so nothing here has been checked against live prices.`}
      </p>

      {s.flags?.length > 0 && (
        <>
          <div className="rx-label" style={{ marginTop: 18, marginBottom: 6 }}>Flags</div>
          {s.flags.map((f, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <span className="rx-tag rx-tag-review" style={{ flexShrink: 0 }}>Review</span>
              <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-2)" }}>{f}</span>
            </div>
          ))}
        </>
      )}

      {s.events?.length > 0 && (
        <>
          <div className="rx-label" style={{ marginTop: 18, marginBottom: 6 }}>
            History ({s.events.length})
          </div>
          {s.events.map((e, i) => (
            <div key={i} className="rx-num rx-faint" style={{ fontSize: 11, padding: "3px 0" }}>
              {e.at ? e.at.slice(0, 16).replace("T", " ") : DASH} · {e.type}
              {e.trades ? ` · ${fmtInt(e.trades)} trades` : ""}
            </div>
          ))}
        </>
      )}

      <div className="rx-row" style={{ marginTop: 18, gap: 8 }}>
        <button type="button" className="rx-btn" disabled
                title="Wired up in Phase 4, with the forward-test queue">
          Queue forward test
        </button>
        <button type="button" className="rx-btn rx-btn-ghost" disabled
                title="Wired up in Phase 4">
          Retire
        </button>
      </div>
      <div className="rx-faint" style={{ fontSize: 10.5, marginTop: 6 }}>
        Actions are read-only until the forward-test queue exists.
      </div>
    </aside>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */

const COLUMNS = [
  { key: "name", label: "Strategy", sortable: true, align: "left" },
  { key: "stage", label: "Stage", sortable: false, align: "left" },
  { key: "trades", label: "Trades", sortable: true, align: "right" },
  { key: "net", label: "Net/trade", sortable: true, align: "right" },
  { key: "tpp", label: "Trades/pair", sortable: false, align: "right", cls: "rx-col-md" },
  { key: "wr", label: "Win %", sortable: false, align: "right", cls: "rx-col-md" },
  { key: "sharpe", label: "Sharpe", sortable: true, align: "right", cls: "rx-col-lg" },
  { key: "gates", label: "Gates", sortable: true, align: "left" },
];

export default function ResearchStrategies({ initialKey, onSelect }) {
  const [stage, setStage] = useState("");
  const [minTrades, setMinTrades] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("gates");
  const [dir, setDir] = useState("desc");
  const [selected, setSelected] = useState(initialKey || null);
  // 271 records rendered at once made a 15,800px page. Cap the rows and say
  // exactly what is hidden rather than silently truncating.
  const [limit, setLimit] = useState(50);

  const { data, error, loading } = useStrategies({ sort, dir });

  const items = useMemo(() => {
    let rows = data?.items || [];
    if (stage) rows = rows.filter((r) => r.stage === stage);
    if (minTrades) rows = rows.filter((r) => (r.backtest?.trades || 0) >= Number(minTrades));
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      rows = rows.filter((r) => (r.name || "").toLowerCase().includes(n)
        || (r.author || "").toLowerCase().includes(n));
    }
    return rows;
  }, [data, stage, minTrades, q]);

  const shown = useMemo(() => items.slice(0, limit), [items, limit]);

  const current = useMemo(
    () => (data?.items || []).find((r) => r.key === selected) || null,
    [data, selected],
  );

  const counts = data?.counts || {};

  React.useEffect(() => { setLimit(50); }, [stage, minTrades, q, sort, dir]);

  const toggleSort = (key) => {
    if (sort === key) setDir(dir === "desc" ? "asc" : "desc");
    else { setSort(key); setDir("desc"); }
  };

  const pick = (r) => {
    setSelected(r.key);
    if (onSelect) onSelect(r.key);
  };

  if (loading) return <div className="rx-empty">Loading strategies…</div>;
  if (error && !data) {
    return <div className="rx-empty">Strategies could not be loaded ({error}).</div>;
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="rx-row" style={{ alignItems: "center", marginBottom: 14, gap: 10 }}>
        <div className="rx-row" style={{ gap: 6, flex: "1 1 auto" }}>
          {STAGE_CHIPS.map(([value, label]) => {
            const n = value ? counts[value] : counts.all;
            if (value && !n) return null;     // don't offer an empty filter
            return (
              <button key={value || "all"} type="button" className="rx-chip"
                      aria-pressed={stage === value}
                      onClick={() => setStage(value)}>
                {label} <span className="rx-num rx-faint">{n == null ? DASH : fmtInt(n)}</span>
              </button>
            );
          })}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
          <span className="rx-label" style={{ fontSize: 11 }}>Min trades</span>
          <select className="rx-input" value={minTrades}
                  onChange={(e) => setMinTrades(e.target.value)}>
            <option value="">Any</option>
            <option value="30">30+</option>
            <option value="100">100+</option>
          </select>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className="rx-label" style={{ fontSize: 11 }}>Search</span>
          <input className="rx-input" type="search" value={q}
                 onChange={(e) => setQ(e.target.value)}
                 placeholder="Name or author" />
        </label>
      </div>

      <div className={`rx-row${current ? " rx-has-detail" : ""}`}
           style={{ alignItems: "flex-start", gap: 14, flexWrap: "nowrap" }}>
        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <div className="rx-card rx-scroll-x" style={{ padding: 0 }}>
            <table className="rx-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={c.cls} style={{ textAlign: c.align }}
                        aria-sort={sort === c.key ? (dir === "asc" ? "ascending" : "descending") : "none"}>
                      {c.sortable
                        ? <button type="button" onClick={() => toggleSort(c.key)}>
                            {c.label}{sort === c.key ? (dir === "asc" ? " ↑" : " ↓") : ""}
                          </button>
                        : c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const bt = r.backtest || {};
                  const tpp = bt.trades && bt.profitable_pairs ? bt.trades / bt.profitable_pairs : null;
                  return (
                    <tr key={r.key} aria-selected={selected === r.key}
                        tabIndex={0} role="button"
                        onClick={() => pick(r)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(r); } }}>
                      <td>
                        <div className="rx-strategy-name" style={{ fontWeight: 600 }}>
                          {r.name || <span className="rx-faint">Unnamed</span>}
                        </div>
                        <div className="rx-faint" style={{ fontSize: 11 }}>
                          {r.author || "author unknown"}
                          {r.flags?.length > 0 && (
                            <span style={{ color: "var(--warn)" }}> · {r.flags.length} flag{r.flags.length > 1 ? "s" : ""}</span>
                          )}
                        </div>
                      </td>
                      <td><StageChip stage={r.stage} /></td>
                      <td className="rx-num-cell" data-label="Trades">{bt.trades == null ? DASH : fmtInt(bt.trades)}</td>
                      <td className="rx-num-cell" data-label="Net/trade"><NetCell v={bt.net_per_trade_pct} /></td>
                      <td className="rx-num-cell rx-col-md" data-label="Trades/pair">{tpp == null ? DASH : fmtNum(tpp, 1)}</td>
                      <td className="rx-num-cell rx-col-md" data-label="Win rate">{bt.win_rate == null ? DASH : fmtRate(bt.win_rate)}</td>
                      <td className="rx-num-cell rx-col-lg" data-label="Sharpe">{bt.sharpe == null ? DASH : fmtNum(bt.sharpe, 2)}</td>
                      <td><Gates gates={r.gates} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!items.length && (
              <div className="rx-empty" style={{ margin: 14 }}>
                No strategy matches these filters. {counts.all
                  ? `The registry holds ${fmtInt(counts.all)} in total.`
                  : "The registry is empty."}
              </div>
            )}
          </div>
          {items.length > shown.length && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
              <button type="button" className="rx-btn rx-btn-ghost"
                      onClick={() => setLimit((n) => n + 100)}>
                Show 100 more ({fmtInt(items.length - shown.length)} hidden)
              </button>
            </div>
          )}
          <div className="rx-faint" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
            Gates, left to right: sample · edge · breadth · forward sample · holds up · unique.
            Filled = pass, outline = fail, grey = not yet knowable. Showing{" "}
            {fmtInt(shown.length)} of {fmtInt(items.length)} matching
            {items.length !== (counts.all || 0) ? ` (${fmtInt(counts.all || 0)} in the registry)` : ""}.
          </div>
        </div>

        <Detail s={current} bar={data?.deploy_bar} onClose={() => setSelected(null)} />
      </div>
    </div>
  );
}
